import { useEffect, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import {
  UseImportStatus_ImportProgressQuery,
  UseImportStatus_ImportProgressQueryVariables,
} from "../../../__generated__/graphql";
import { IImportProgress } from "./AutomationListImportContactsModal/useImportState";

interface IImportStatus {
  progress: IImportProgress | null;
  status: "processing" | "completed" | "failed" | null;
  isActive: boolean;
  dismissBanner: () => void;
}

const UseImportStatus_ImportProgress = gql`
  query UseImportStatus_ImportProgress($listId: Int!) {
    importContactsProgress(listId: $listId) {
      id
      processedCount
      totalCount
      duplicateContacts {
        firstName
        lastName
        title
        companyName
        companyWebsiteDomain
        companyLinkedinUrl
      }
      updatedContacts {
        id
      }
      completedContacts {
        id
      }
      success
      errors
      status
    }
  }
`;

export const useImportStatus = (
  listId: number,
  onImportComplete?: () => void,
): IImportStatus => {
  const [importStatus, setImportStatus] = useState<
    Omit<IImportStatus, "dismissBanner">
  >({
    progress: null,
    status: null,
    isActive: false,
  });

  const [getImportProgress, { data: progressData, stopPolling }] = useLazyQuery<
    UseImportStatus_ImportProgressQuery,
    UseImportStatus_ImportProgressQueryVariables
  >(UseImportStatus_ImportProgress, {
    pollInterval: 2000, // Poll every 2 seconds (less frequent than modal)
    fetchPolicy: "network-only",
  });

  // Check for active import job on mount
  useEffect(() => {
    // Start polling for this job
    getImportProgress({
      variables: { listId },
    });
    setImportStatus((prev) => ({ ...prev, isActive: true }));
  }, [listId, getImportProgress]);

  // Handle progress updates
  useEffect(() => {
    if (progressData?.importContactsProgress) {
      const progress = progressData.importContactsProgress;

      const mappedProgress: IImportProgress = {
        id: progress.id,
        processedCount: progress.processedCount,
        totalCount: progress.totalCount,
        duplicateContacts: progress.duplicateContacts || [],
        completedContacts: progress.completedContacts || [],
        updatedContacts: progress.updatedContacts || [],
        errors: progress.errors || [],
      };

      setImportStatus({
        progress: mappedProgress,
        status: progress.status as "processing" | "completed" | "failed",
        isActive: true,
      });

      // If job is completed or failed, stop polling but keep banner visible
      if (progress.status === "completed" || progress.status === "failed") {
        stopPolling();

        // Call the completion callback if import was successful
        if (progress.status === "completed" && onImportComplete) {
          onImportComplete();
        }
      }
    }
  }, [progressData, stopPolling, listId, onImportComplete]);

  const dismissBanner = () => {
    setImportStatus({
      progress: null,
      status: null,
      isActive: false,
    });
  };

  return {
    ...importStatus,
    dismissBanner,
  };
};
